import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Form, Field } from 'react-final-form';
import { connect } from 'react-redux';
import { SimpleSelectField, Button } from '@unite-us/app-components';
import { fetchReferralPrograms } from 'actions/Program/Group';
import { reassignReferralProgram } from 'actions/Program/Referral/Group';
import { OverlaySpinner, Spinner } from 'common/spinners';
import { isDraft } from 'src/components/Referrals/ReferralStatus/utils/status';
import { isOptionUpdated } from 'src/components/Referrals/ReferralFormFields/utils/selectField';
import {
  hasPaymentsUserAccess,
  includePathwaysServices,
} from 'src/common/utils/FeatureFlags/flags';
import { buildContractedProgramOptions } from 'src/components/Referrals/ReferralStatus/utils';
import { required } from 'src/common/form/utils/rffValidations';

function buildProgramOptions(programs, usePaymentsRole) {
  if (usePaymentsRole) {
    return programs.map((program) => buildContractedProgramOptions(program, 'name'));
  }
  return programs.map((p) => ({ name: p.attributes.name, id: p.id }));
}

export function ReferralProgramForm({
  referral, closeModal, dispatch, usePaymentsUserRole, includePathways, groupId,
}) {
  const [programs, setPrograms] = useState([]);
  const [isFetchingPrograms, setIsFetchingPrograms] = useState(true);

  const prevServiceTypeRef = useRef(_.get(referral, 'service_type', {}));

  const fetchPrograms = useCallback((ref) => {
    const programId = _.get(ref, 'referred_to_program.id');

    const fetchGroupsProgramsOptions = {
      excludeBillable: false,
      excludeAuthorizationRequired: true,
      includePathways,
    };

    dispatch(fetchReferralPrograms(ref, groupId, fetchGroupsProgramsOptions))
      .then((payload) => {
        const fetchedPrograms = _.get(payload, 'data.data', []);
        if (_.findIndex(fetchedPrograms, { id: programId }) < 0) {
          dispatch(fetchReferralPrograms(ref, groupId, { ids: programId }))
            .then((response) => {
              const program = _.get(response, 'data.data', []);
              setPrograms([...program, ...fetchedPrograms]);
              setIsFetchingPrograms(false);
            });
        } else {
          setPrograms(fetchedPrograms);
          setIsFetchingPrograms(false);
        }
      });
  }, [dispatch, groupId, includePathways]);

  // Only fetch programs on mount. The second useEffect below handles
  // subsequent referral/service-type changes.
  useEffect(() => {
    if (!isDraft(referral)) {
      fetchPrograms(referral);
    }
  }, []);

  useEffect(() => {
    const nextServiceType = _.get(referral, 'service_type', {});
    if (isOptionUpdated(prevServiceTypeRef.current, nextServiceType)) {
      prevServiceTypeRef.current = nextServiceType;
      if (!isDraft(referral)) {
        fetchPrograms(referral);
      }
    }
  }, [referral, fetchPrograms]);

  const onCancel = () => {
    if (closeModal) closeModal();
  };

  const onSubmit = (values) => {
    const referralId = referral.id;
    const programId = _.get(values, 'referred_to_program');

    const params = {
      referral: {
        referred_to_program_id: programId,
      },
    };

    return dispatch(reassignReferralProgram(referralId, groupId, params))
      .then(() => {
        onCancel();
      });
  };

  if (isFetchingPrograms) {
    return <Spinner text="Retrieving Programs..." />;
  }

  const programOptions = buildProgramOptions(programs, usePaymentsUserRole);
  const initialValues = { referred_to_program: _.get(referral, 'referred_to_program.id', '') };

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      render={({ handleSubmit, submitting }) => (
        <form
          className="referral-program-form content-with-actions"
          onSubmit={handleSubmit}
        >
          <div className="content-container content-container--medium">
            <OverlaySpinner text="Updating Program..." show={submitting} />

            <Field name="referred_to_program" validate={required}>
              {({ input, meta }) => (
                <SimpleSelectField
                  className="referral-program-form__select-field"
                  id="referral-program-select"
                  label="Programs"
                  value={input.value}
                  onChange={input.onChange}
                  onBlur={input.onBlur}
                  options={programOptions}
                  optionSettings={{ labelKey: 'name', valueKey: 'id' }}
                  error={meta.touched ? meta.error : undefined}
                  required
                />
              )}
            </Field>
          </div>
          <div className="actions">
            <span className="action-item">
              <Button
                id="reject-referral-cancel-btn"
                onClick={onCancel}
                disabled={submitting}
                label="Cancel"
              />
            </span>
            <span className="action-item">
              <Button
                id="reject-referral-reject-btn"
                onClick={handleSubmit}
                disabled={submitting}
                label="Save"
                primary
              />
            </span>
          </div>
        </form>
      )}
    />
  );
}

ReferralProgramForm.propTypes = {
  referral: PropTypes.object.isRequired,
  closeModal: PropTypes.func,
  dispatch: PropTypes.func.isRequired,
  usePaymentsUserRole: PropTypes.bool.isRequired,
  includePathways: PropTypes.bool.isRequired,
  groupId: PropTypes.string.isRequired,
};

ReferralProgramForm.defaultProps = {
  closeModal: _.noop,
};

function mapStateToProps(state) {
  return {
    usePaymentsUserRole: hasPaymentsUserAccess(state),
    includePathways: includePathwaysServices(state),
    groupId: _.get(state, 'session.groupId', ''),
  };
}

export default connect(mapStateToProps)(ReferralProgramForm);
